import { add, format, startOfDay } from 'date-fns';
import Flight from '../Flight';
import VehicleType from '../VehicleType';
import Address from '../Address';
import Airport from '../Airport';
import Passenger from '../Passenger';
import Addon from '../Addon';
import LuggageCounter from '../LuggageCounter';
import { MAX_PASSENGER_COUNT, ORDER_TYPE_ARRIVAL, ORDER_TYPE_DEPARTURE } from '@/data';

const DEFAULT_PICK_UP_TIME = () => null;
const DEFAULT_RAW_FLIGHT_DATE = () =>
  format(add(startOfDay(new Date()), { days: 3 }), 'yyyy/MM/dd');

export default class PlacementOrder {
  orderType = 'arrival';
  pickUpOrDropOffLocation = new Address();
  airportLocation = new Airport();
  isPriceTableLoading = false;

  /**
   * @type {Date}
   */
  pickUpTime = DEFAULT_PICK_UP_TIME();
  /**
   * @type {Flight | null}
   */
  flightInformation = null;

  // 起飛
  /**
   * @type {string} YYYY/MM/DD
   */
  rawFlightDate = DEFAULT_RAW_FLIGHT_DATE();
  /**
   * @type {string}
   */
  rawFlightNumber = '';

  passengerCount = 1;
  luggageCounter = new LuggageCounter();
  vehicleType = new VehicleType();
  description = '';

  /**
   * @type {Array<Addon>}
   */
  addons = []; // Array<Addon>

  /**
   * @type {Array<Address>}
   */
  extraStops = [];
  isExtraStopsValid = true;

  passenger = new Passenger();
  remoteAreaSurchargeId = '';

  /**
   * null 代表不使用
   * string 代表使用
   * @type {string | null}
   */
  couponId = '';

  get defaultPickUpTime() {
    return DEFAULT_PICK_UP_TIME();
  }

  get defaultRawFlightDate() {
    return DEFAULT_RAW_FLIGHT_DATE();
  }
  // estimate value
  /**
   * @deprecated
   */
  get totalAmount() {
    return (
      this.vehicleType.price +
      this.addons.reduce((acc, addon) => acc + addon.price, 0) +
      this.extraStops.length * 200
    );
  }

  get isLocationTimeFlightInformationValid() {
    return Boolean(
      this.pickUpOrDropOffLocation.city &&
        this.airportLocation.name &&
        this.pickUpTime &&
        this.rawFlightDate &&
        this.rawFlightNumber.length >= 3 &&
        this.flightInformation,
    );
  }

  get isDetailInformationValid() {
    return Boolean(this.vehicleType.isValid && this.passenger.isValid);
  }

  get flightAt() {
    // 當航班資訊失敗時，使用 rawFlightDate 作為航班時間
    if (!this.flightInformation || this.flightInformation.isEmpty) {
      if (this.orderType === ORDER_TYPE_DEPARTURE) {
        // 起飛 依據用戶填寫當地起飛時間
        const pickUpTime = new Date(this.rawFlightDate);
        return format(pickUpTime, 'yyyy-MM-dd HH:mm');
      } else if (this.orderType === ORDER_TYPE_ARRIVAL) {
        // 回國 依據用戶填寫當地起飛時間
        // this.rawFlightDate 格式為 YYYY/MM/DD HH:mm
        return this.rawFlightDate.replace(/\//g, '-');
      } else {
        throw new Error('Invalid order type');
      }
    }

    // 依據航班資訊為主
    if (this.orderType === ORDER_TYPE_DEPARTURE) {
      return this.flightInformation.formatDepartureDateTime;
    } else if (this.orderType === ORDER_TYPE_ARRIVAL) {
      // 回國 此時間為落地時間
      return this.flightInformation.formatArrivalDateTime;
    } else {
      throw new Error('Invalid order type');
    }
  }

  constructor({
    orderType = ORDER_TYPE_DEPARTURE,
    pickUpOrDropOffLocation = new Address(),
    airportLocation = new Airport(),
    pickUpTime = DEFAULT_PICK_UP_TIME(),
    flightInformation = null,
    passengerCount = 1,
    luggageCounter = new LuggageCounter(),
    vehicleType = new VehicleType(),
    description = '',
    addons = [],
    extraStops = [],
    isExtraStopsValid = true,
    passenger = new Passenger(),
    remoteAreaSurchargeId = '',
    rawFlightDate = DEFAULT_RAW_FLIGHT_DATE(),
    rawFlightNumber = '',
    couponId = '',
    isPriceTableLoading = false,
  } = {}) {
    this.orderType = orderType;
    this.pickUpOrDropOffLocation = pickUpOrDropOffLocation;
    this.airportLocation = airportLocation;
    this.pickUpTime = pickUpTime;
    this.flightInformation = flightInformation;
    this.passengerCount = passengerCount;
    this.luggageCounter = luggageCounter;
    this.vehicleType = vehicleType;
    this.description = description;
    this.addons = addons;
    this.extraStops = extraStops;
    this.isExtraStopsValid = isExtraStopsValid;
    this.passenger = passenger;
    this.remoteAreaSurchargeId = remoteAreaSurchargeId;
    this.rawFlightDate = rawFlightDate;
    this.rawFlightNumber = rawFlightNumber;
    this.couponId = couponId;
    this.isPriceTableLoading = isPriceTableLoading;
  }

  clone() {
    const newAddons = this.addons.map((addon) => addon.clone());

    return new PlacementOrder({
      orderType: this.orderType,
      pickUpOrDropOffLocation: this.pickUpOrDropOffLocation.clone(),
      airportLocation: this.airportLocation.clone(),
      pickUpTime: this.pickUpTime,
      flightInformation: this.flightInformation?.clone() ?? null,
      passengerCount: this.passengerCount,
      luggageCounter: this.luggageCounter.clone(),
      vehicleType: this.vehicleType.clone(),
      description: this.description,
      addons: newAddons,
      extraStops: this.extraStops.map((address) => address?.clone() ?? null),
      isExtraStopsValid: this.isExtraStopsValid,
      passenger: this.passenger.clone(),
      remoteAreaSurchargeId: this.remoteAreaSurchargeId,
      rawFlightDate: this.rawFlightDate,
      rawFlightNumber: this.rawFlightNumber,
      couponId: this.couponId,
    });
  }

  shallowClone() {
    return new PlacementOrder(this);
  }

  setOrderType(orderType) {
    const nextPlacementOrder = this.shallowClone();

    if (![ORDER_TYPE_ARRIVAL, ORDER_TYPE_DEPARTURE].includes(orderType)) {
      return nextPlacementOrder;
    }

    nextPlacementOrder.orderType = orderType;
    return nextPlacementOrder;
  }

  setPickUpOrDropOffLocation(address) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.pickUpOrDropOffLocation = address;
    return nextPlacementOrder;
  }

  setAirportLocation(airport) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.airportLocation = airport;
    return nextPlacementOrder;
  }

  setPickUpTime(pickUpTime) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.pickUpTime = pickUpTime;
    return nextPlacementOrder;
  }

  setFlightInformation(flightInformation) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.flightInformation = flightInformation;
    return nextPlacementOrder;
  }

  setPassengerCount(passengerCount) {
    const nextPlacementOrder = this.shallowClone();

    if (passengerCount > MAX_PASSENGER_COUNT) {
      return nextPlacementOrder;
    }

    nextPlacementOrder.passengerCount = passengerCount;
    return nextPlacementOrder;
  }

  setLuggageCounter(luggageCounter) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.luggageCounter = luggageCounter;
    return nextPlacementOrder;
  }

  setVehicleType(vehicleType) {
    const nextPlacementOrder = this.shallowClone();

    if (!vehicleType) {
      nextPlacementOrder.vehicleType = new VehicleType();
      return nextPlacementOrder;
    }

    nextPlacementOrder.vehicleType = vehicleType;
    return nextPlacementOrder;
  }

  setDescription(description) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.description = description;
    return nextPlacementOrder;
  }

  setAddon(addon) {
    const nextPlacementOrder = this.shallowClone();

    // if addon count === 0 then remove the addon
    if (addon.count === 0) {
      nextPlacementOrder.addons = this.addons.filter((a) => a.id !== addon.id);
      return nextPlacementOrder;
    }

    const existAddonIndex = this.addons.findIndex((a) => a.id === addon.id);
    const nextAddons = [...nextPlacementOrder.addons];
    if (existAddonIndex !== -1) {
      nextAddons[existAddonIndex] = addon;
    } else {
      nextAddons.push(addon);
    }
    nextPlacementOrder.addons = nextAddons;
    return nextPlacementOrder;
  }

  setPassenger(passenger) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.passenger = passenger;
    return nextPlacementOrder;
  }

  setExtraStops(extraStops) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.extraStops = extraStops;
    return nextPlacementOrder;
  }

  setIsPriceTableLoading(nextIsPriceTableLoading) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.isPriceTableLoading = nextIsPriceTableLoading;
    return nextPlacementOrder;
  }

  setIsExtraStopsValid(isValid) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.isExtraStopsValid = isValid;
    return nextPlacementOrder;
  }

  addExtraStop(address) {
    const nextPlacementOrder = this.shallowClone();
    const nextExtraStops = [...nextPlacementOrder.extraStops];
    nextExtraStops.push(address);
    nextPlacementOrder.extraStops = nextExtraStops;
    return nextPlacementOrder;
  }

  removeExtraStop(index) {
    const nextPlacementOrder = this.shallowClone();
    const nextExtraStops = [...nextPlacementOrder.extraStops];
    nextExtraStops.splice(index, 1);
    nextPlacementOrder.extraStops = nextExtraStops;
    return nextPlacementOrder;
  }

  setRemoteAreaSurchargeId(remoteAreaSurchargeId) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.remoteAreaSurchargeId = remoteAreaSurchargeId;
    return nextPlacementOrder;
  }

  setRawFlightDate(rawFlightDate) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.rawFlightDate = rawFlightDate;
    return nextPlacementOrder;
  }

  setRawFlightNumber(rawFlightNumber) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.rawFlightNumber = rawFlightNumber;
    return nextPlacementOrder;
  }

  setCouponId(couponId) {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.couponId = couponId;
    return nextPlacementOrder;
  }

  /**
   * @returns {FormData}
   */
  getFormData() {
    const formData = new FormData();
    // 估價時使用
    formData.append('vehicle_id', this.vehicleType.id);
    formData.append('remote_area_surcharge_id', this.remoteAreaSurchargeId);
    formData.append('passenger_count', this.passengerCount);
    formData.append('luggage_20_inch_count', this.luggageCounter.luggage20InchCount);
    formData.append('luggage_25_inch_count', this.luggageCounter.luggage25InchCount);
    formData.append('luggage_27_inch_count', this.luggageCounter.luggage27InchCount);
    formData.append('luggage_29_inch_count', this.luggageCounter.luggage29InchCount);
    this.addons.forEach((addon, index) => {
      formData.append(`addons[${index}][id]`, addon.id);
      formData.append(`addons[${index}][count]`, addon.count);

      // 延伸：建立訂單時使用
      if (addon.type === 'extra_stop') {
        if (Array.isArray(addon.content)) {
          addon.content.forEach((address, i) => {
            formData.append(`addons[${index}][content][${i}]`, address);
          });
        }
      } else if (addon.content) {
        formData.append(`addons[${index}][content]`, addon.content);
      }
    });
    formData.append('pickup_at', format(this.pickUpTime, 'yyyy-MM-dd HH:mm'));

    // 延伸：建立訂單時使用
    formData.append('has_egate', '0');
    formData.append('id_type', 'identity');
    // formData.append("id_number", this.passenger.identityNumber)
    formData.append('passenger_name', this.passenger.name);
    formData.append('passenger_email', this.passenger.email);
    formData.append('passenger_phone_country_code', '886'); //TODO: fixed val
    formData.append('passenger_phone_number', this.passenger.phoneNumber.slice(1)); // NO leading zero
    formData.append('flight_direction', this.orderType);
    formData.append('flight_at', this.flightAt);
    formData.append('flight_number', this.rawFlightNumber);
    formData.append('pickup_or_dropoff_zip_code', this.pickUpOrDropOffLocation.zipCode.slice(0, 3));
    formData.append('pickup_or_dropoff_city', this.pickUpOrDropOffLocation.city);
    formData.append('pickup_or_dropoff_area', this.pickUpOrDropOffLocation.area);
    formData.append('pickup_or_dropoff_address', this.pickUpOrDropOffLocation.address);
    formData.append('special_instructions', this.description);

    // for afc
    formData.append('airport_location_id', this.airportLocation.id);

    if (this.couponId) {
      formData.set('coupon_code_id', this.couponId);
    }

    if (this.couponId === null) {
      formData.set('coupon_code_id', '');
    }

    return formData;
  }
}
